'use client';

import { variables } from './variables';
import { createTheme, Shadows } from '@mui/material/styles';
import type {} from '@mui/lab/themeAugmentation';
import { addOpacityToThemeColor } from 'utils/styles/addOpacity';
import { createBreakpoints } from '@mui/system';
import sharedTheme from '../shared/theme';

import { SuccessIcon } from '@/assets/theme/agc/alertIcons/SuccessIcon';
import { WarningIcon } from '@/assets/theme/agc/alertIcons/WarningIcon';
import { ErrorIcon } from '@/assets/theme/agc/alertIcons/ErrorIcon';
import { InfoIcon } from '@/assets/theme/agc/alertIcons/InfoIcon';

const { colors, fonts, borderRadius, padding, boxShadows, typography } = variables;

const stepperLineBackground = (color: string) =>
    `50% 50% no-repeat url("data:image/svg+xml,%3Csvg width='35' height='9' viewBox='0 0 35 9' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath opacity='0.5' d='M34.3536 4.85355C34.5488 4.65829 34.5488 4.34171 34.3536 4.14645L31.1716 0.964466C30.9763 0.769204 30.6597 0.769204 30.4645 0.964466C30.2692 1.15973 30.2692 1.47631 30.4645 1.67157L33.2929 4.5L30.4645 7.32843C30.2692 7.52369 30.2692 7.84027 30.4645 8.03553C30.6597 8.2308 30.9763 8.2308 31.1716 8.03553L34.3536 4.85355ZM0 5H34V4H0V5Z' fill='${color}' /%3E%3C/svg%3E%0A")`;

const breakpoints = createBreakpoints({});

const defaultTheme = sharedTheme(variables);

const outlinedInputStyles = {
    styleOverrides: {
        root: {
            ...defaultTheme.MuiOutlinedInput.styleOverrides.root,
            color: colors.palette.text.dark,
            borderRadius: borderRadius.secondary,

            '&.Mui-focused': {
                '& .MuiOutlinedInput-notchedOutline, & .MuiPickersOutlinedInput-notchedOutline': {
                    borderWidth: '1px',
                    borderColor: colors.other.inputs.focus,
                    boxShadow: boxShadows.inputShadow,
                },
            },
        },
    },
};

export const theme = createTheme({
    colors,
    fonts,
    borderRadius,
    padding,
    palette: colors.palette,
    typography,
    components: {
        MuiTypography: {
            styleOverrides: {
                root: {
                    lineHeight: '140%',
                    '&.badgeDefinitionTitle': {
                        margin: '8px 0',
                    },
                    '&.badgeDefinitionDescription': {
                        fontWeight: '400',
                        color: colors.palette.text.primary,
                        textTransform: 'none',
                    },
                    '&.surveyCardTitle, &.surveyCardProjectNumber, &.surveyDetailsValue': {
                        color: colors.palette.text.primary,
                    },
                    '&.surveyDetailsValue': {
                        textTransform: 'none',
                        marginRight: '16px',
                        fontWeight: '600',
                    },
                    '&.passwordDescriptionContent': {
                        fontSize: '0.875rem',
                        color: colors.palette.gray.dark4,
                        fontWeight: '400',
                    },
                    '&.passwordDescriptionContent ul': {
                        paddingLeft: '24px',
                    },
                    '&.MuiCardHeader-title': {
                        fontSize: '1rem',
                        fontWeight: '600',
                    },
                    '&.nmDashboardWelcomeText': {
                        fontWeight: 'normal',
                    },
                    '&.nmDashboardNote': {
                        fontWeight: 'normal',
                        fontSize: '0.75rem',
                    },
                    '&.nmSurveyCardLabel': {
                        fontWeight: 'normal',
                        color: 'rgba(127, 136, 152, 1)',
                    },
                    '&.dynamicStaticPagesLink': {
                        fontWeight: '400',
                        textTransform: 'none',
                    },
                    '&.ProfileBenefitTitle': {
                        color: colors.palette.text.primary,
                        fontWeight: '600',
                    },
                    '&.referralDetailSubtitle': {
                        display: 'inline-flex',
                        color: colors.palette.gray.dark4,
                        fontSize: '0.875rem',
                        marginBottom: '0.25rem',
                    },
                    '&.referralCopyLink': {
                        color: colors.palette.primary.dark,
                    },
                    '&.passwordRequirmentsTitle': {
                        color: colors.palette.gray.dark4,
                    },
                },
            },
        },
        MuiBackdrop: defaultTheme.MuiBackdrop,
        MuiCssBaseline: {
            styleOverrides: {
                ...defaultTheme.MuiCssBaseline.styleOverrides,
                'strong, b': {
                    fontWeight: '600',
                },
                '&.MuiBox-root.introRegistrationWrapper': {
                    paddingBottom: '48px',
                },
                '&.language-info .MuiBox-root, &.language-button .MuiBox-root': {
                    fontSize: '1rem',
                    fontWeight: '400',
                },
                '&.triangleArrowUp': {
                    borderBottom: '5px solid rgb(102,102,102)',
                },
                '&.triangleArrowDown': {
                    borderTop: '5px solid rgb(102,102,102)',
                },
                '.topMenuLinksList': {
                    position: 'absolute',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    a: {
                        textDecoration: 'none',
                    },
                },
                '&.verificationFormElementBlock ': {
                    paddingRight: '8px',
                    paddingLeft: '8px',
                },
                '&.verificationSubmitButtonWrapper': {
                    paddingRight: '8px',
                },
                '&.paymentMethodComingSoonLabel': {
                    color: colors.other.text.info,
                    background: 'rgb(229, 246, 253)',
                    padding: '2px 6px',
                    top: '0',
                    right: '0',
                },
                '&.referralInfoWrapper': {
                    paddingLeft: '8px',
                    paddingTop: '8px',
                },
                '.privacyterms': {
                    fontSize: '17px',
                    fontWeight: '600',
                    paddingBottom: '10px',
                },
                '&.WalletMinMaxLabel': {
                    backgroundColor: colors.other.chip.default.background,
                    color: colors.palette.text.primary,
                    borderRadius: borderRadius.primary,
                },
                '.HeaderLogoNewYearAnimation': {
                    width: '250px',
                },
                '.AccountUserIdBlock ': {
                    backgroundColor: colors.other.background.footer,
                    borderRadius: borderRadius.primary,
                },
                '.AccountUserId': {
                    fontSize: '16px',
                    fontWeight: '600',
                    color: colors.palette.text.primary,
                },
                '&.introTextBlock': {
                    backgroundColor: colors.palette.primary.dark3,
                    borderRadius: borderRadius.tertiary,
                    padding: '32px 24px 252px 24px',
                    position: 'relative',
                    '.homePageTitle': {
                        fontSize: '1.75rem',
                        fontWeight: '600',
                        color: colors.palette.primary.light,
                        lineHeight: '120%',
                    },
                    '.homePageDescription': {
                        fontSize: '0.9375rem',
                        color: 'white',
                        lineHeight: '130%',
                    },
                    '.introPicBlock': {
                        width: '100%',
                        height: '220px',
                        position: 'absolute',
                        bottom: '0',
                        left: '0',
                        lineHeight: '0',
                        borderRadius: `0 0 ${borderRadius.tertiary} ${borderRadius.tertiary}`,
                        overflow: 'hidden',
                        background: 'transparent',
                        border: 'none',
                        img: {
                            width: 'auto',
                            maxWidth: '100%',
                            maxHeight: '100%',
                            objectFit: 'contain',
                        },
                    },
                },
                '&.introIcons': {
                    backgroundColor: 'transparent',
                },
                '&.introStack': {
                    justifyContent: 'space-between',
                    marginTop: '-12px',
                    '.introDescription': {
                        color: colors.palette.text.primary,
                    },
                    '.homeIntroIconsItem': {
                        flexBasis: '100%',
                        maxWidth: '100%',
                        borderRadius: borderRadius.primary,
                        background: colors.palette.gray.light,
                        margin: '12px 0 0',
                        '> div': {
                            width: '100%',
                        },
                    },
                },
                '&.Header': {
                    '.headerWrapper': {
                        paddingTop: '1px',
                        paddingBottom: '1px',
                    },
                    '.HeaderPanelImageLogo': {
                        width: '99px',
                        height: '46px',
                    },
                    '.HeaderLogo, .HeaderLogo a': {
                        width: '100%',
                        height: '100%',
                    },
                    '.HeaderLogo a': {
                        display: 'inline-flex',
                        background:
                            'url("/images/themes/agc/logo-header-mobile.svg") no-repeat 0 0',
                    },
                    '.HeaderLogo img': {
                        width: '100%',
                        height: 'auto',
                        display: 'none',
                    },
                },
                '&.Footer': {
                    '.MuiStack-root': {
                        alignItems: 'flex-start',
                    },
                    '.FooterLogo': {
                        paddingTop: '16px',
                        paddingBottom: '16px',
                        'a img': {
                            width: '231px',
                            height: '52px',
                        },
                    },
                    '.footerMenu': {
                        flexDirection: 'column',
                        li: {
                            textAlign: 'left',
                            marginBottom: '8px',
                        },
                    },
                    '.copyright': {
                        textAlign: 'left',
                    },
                },
                [breakpoints.up('sm')]: {
                    '&.Footer': {
                        '.MuiStack-root': {
                            alignItems: 'flex-start',
                        },
                        '.footerMenu': {
                            flexDirection: 'row',
                        },
                        '.FooterLogo': {
                            paddingTop: '0',
                        },
                    },
                },
                [breakpoints.up('md')]: {
                    '&.introStack': {
                        marginTop: '-24px',
                        '.homeIntroIconsItem': {
                            flexBasis: 'calc(33.3% - 15px)',
                            maxWidth: 'calc(33.3% - 15px)',
                            marginTop: '24px',
                        },
                    },
                    '&.Header': {
                        '.HeaderPanelImageLogo': {
                            width: '231px',
                            height: '52px',
                        },
                        '.HeaderLogo a': {
                            background: 'none',
                        },
                        '.HeaderLogo img': {
                            display: 'block',
                        },
                    },
                },
                [breakpoints.up('lg')]: {
                    '&.introRegistration': {
                        '.introTextBlock': {
                            width: '396px',
                            marginRight: '36px',
                            flexShrink: '0',
                        },
                        '.registrationContainer': {
                            width: '100%',
                        },
                    },
                },
            },
        },
        MuiContainer: {
            styleOverrides: {
                root: {
                    '&.introLeftSide': {
                        padding: '0',
                        margin: '0 0 8px 0',
                        maxWidth: '100%',
                        borderBottom: '1px solid var(--color-light-gray)',
                        [breakpoints.up('md')]: {
                            padding: '0 48px 0 0',
                            margin: '0 24px 0 0',
                            minWidth: '360px',
                            borderBottom: 'none',
                            borderRight: '1px solid var(--color-light-gray)',
                        },
                    },
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    ...defaultTheme.MuiPaper.styleOverrides.root,
                    border: `1px solid ${colors.palette.gray.dark2}`,
                    '&.lang-switcher-wrapper': {
                        maxHeight: 'calc(100% - 32px)',
                    },
                    '&.infoBox': {
                        border: 'none',
                        backgroundColor: 'transparent',
                        paddingBottom: '30px',
                        '.introTitle': {
                            maxWidth: '100%',
                            margin: '10px auto',
                            minHeight: '0',
                        },
                        '.homeIntroIconsItem': {
                            alignItems: 'left',
                            textAlign: 'left',
                            '.introIcon': {
                                alignSelf: 'flex-start',
                                width: 'auto',
                                height: '24px',
                            },
                        },
                    },
                    '&.warningBox': {
                        border: `1px solid ${colors.other.text.warning}`,
                        h3: {
                            fontSize: '1.125rem',
                            color: colors.palette.primary.dark2,
                        },
                    },
                    '&.registrationBox': {
                        border: 'none',
                        margin: '32px 0 0 0',
                        padding: '0',
                        [breakpoints.up('md')]: {
                            margin: '0',
                        },
                    },
                    '&.registrationBox .MuiStepper-horizontal': {
                        marginBottom: '36px',
                    },
                    '&.accountMenuWrapper': {
                        padding: 0,
                        border: 0,
                        background: 'transparent',
                        '.notificationBadge > .MuiBadge-badge': {
                            top: '-2px',
                            right: '-9px',
                        },
                    },
                    '&.accountMenuBox': {
                        padding: 0,
                        border: 'none',
                    },
                    '&.accountTaxDetails': {
                        '.helperText': {
                            position: 'relative',
                            backgroundColor: colors.other.background.footer,
                            padding: '16px',
                            [breakpoints.up('md')]: {
                                marginLeft: '20px',
                                '&::before': {
                                    content: '""',
                                    position: 'absolute',
                                    top: '15px',
                                    left: '-9px',
                                    width: '0',
                                    height: '0',
                                    borderTop: '10px solid transparent',
                                    borderBottom: '10px solid transparent',
                                    borderRight: `10px solid ${colors.other.background.footer}`,
                                    transform: 'translateY(-50%)',
                                },
                            },
                        },
                    },
                    '&.dynamicStaticPagesItem': {
                        transition: 'all 0.15s',
                    },
                    '&.dynamicStaticPagesItem:hover': {
                        borderColor: colors.palette.primary.main,
                    },
                    '.ProfileQuestionsContent': {
                        border: `1px solid ${colors.palette.background.default}`,
                    },
                    '&.WalletUploadFileInput': {
                        ...defaultTheme.MuiPaper.styleOverrides.root['&.WalletUploadFileInput'],
                        borderRadius: borderRadius.secondary,
                    },
                    '.nmSurveyCardInfoIcon svg path': {
                        fill: colors.palette.gray.dark4,
                    },
                    '&.MuiAlert-root': {
                        border: 'none',
                    },
                },
                elevation: {
                    borderRadius: '22px',
                    '::-webkit-scrollbar': {
                        width: '12px',
                    },
                    '::-webkit-scrollbar-track': {
                        borderRadius: '6px',
                        backgroundColor: colors.palette.gray.light,
                        marginTop: '16px',
                        marginBottom: '16px',
                    },
                    '::-webkit-scrollbar-thumb': {
                        backgroundColor: colors.palette.gray.dark2,
                        borderRadius: '6px',
                        ':hover': {
                            backgroundColor: colors.palette.gray.dark3,
                        },
                    },
                },
                elevation1: {
                    borderRadius: '14px',
                },
            },
        },
        MuiPopover: defaultTheme.MuiPopover,
        MuiOutlinedInput: outlinedInputStyles,
        MuiPickersOutlinedInput: outlinedInputStyles,
        MuiChip: {
            styleOverrides: {
                root: { ...defaultTheme.MuiChip.styleOverrides.root, fontWeight: 400 },
            },
        },
        MuiAutocomplete: defaultTheme.MuiAutocomplete,
        MuiSelect: defaultTheme.MuiSelect,
        MuiAccordion: {
            styleOverrides: {
                root: {
                    ...defaultTheme.MuiAccordion.styleOverrides.root,
                    borderRadius: borderRadius.primary,
                    boxShadow: 'none',
                    '&:first-of-type, &:last-of-type': {
                        borderRadius: borderRadius.primary,
                    },
                },
            },
        },
        MuiList: {
            styleOverrides: {
                root: {
                    ...defaultTheme.MuiList.styleOverrides.root,
                    borderRadius: borderRadius.primary,
                    '&.topNavigationMenu': {
                        border: 'none',
                        li: {
                            width: '100%',
                        },
                        a: {
                            width: '100%',
                            padding: '0 16px',
                        },
                        'a:hover': {
                            textDecoration: 'none',
                        },
                    },
                    '&.MuiMenu-list': {
                        border: 'none',
                    },
                    '&.topNavigationMenu .selectOpinionTopMenuItem': {
                        display: 'flex',
                        gap: '0.75rem',
                        color: 'white',
                        borderRadius: borderRadius.primary,
                        backgroundColor: 'rgba(29, 81, 86, 1)',
                    },
                },
            },
        },
        MuiListSubheader: defaultTheme.MuiListSubheader,
        MuiMenu: {
            ...defaultTheme.MuiMenu,
            styleOverrides: {
                root: {
                    '.MuiList-root': {
                        padding: '2px 0',
                    },
                },
            },
        },
        MuiDialogTitle: {
            styleOverrides: {
                root: {
                    color: colors.palette.text.primary,
                },
            },
        },
        MuiMenuItem: {
            styleOverrides: {
                root: {
                    ...defaultTheme.MuiMenuItem.styleOverrides.root,
                    margin: '2px 4px',
                    padding: '6px 12px',
                    borderRadius: borderRadius.secondary,
                    '& a': {
                        fontWeight: '400',
                        color: colors.palette.text.dark,
                    },
                    '&.MuiButtonBase-root.Mui-selected': {
                        color: colors.palette.primary.dark,
                        background: 'transparent',
                    },
                    '&:hover': {
                        color: colors.palette.text.dark,
                        backgroundColor: colors.palette.gray.main,
                    },
                    '&:not(.active):hover': {
                        backgroundColor: colors.palette.gray.main,
                        '& a': {
                            color: colors.palette.text.dark,
                        },
                    },
                    '&.accountMenuItem': {
                        borderRadius: 0,
                        padding: 0,
                        margin: '0 0 8px 0',
                        '& a': {
                            color: colors.palette.text.primary,
                            padding: '9px 20px',
                        },
                        '&:hover': {
                            backgroundColor: 'transparent',
                            '& a': {
                                backgroundColor: colors.palette.primary.main,
                                color: 'white',
                            },
                        },
                        '&:active': {
                            backgroundColor: 'transparent',
                            '& a': {
                                backgroundColor: colors.palette.primary.dark3,
                                color: 'white',
                            },
                        },
                        '&.active': {
                            backgroundColor: 'transparent',
                            '& a': {
                                backgroundColor: colors.palette.primary.dark,
                                color: 'white',
                            },
                            '& a:hover': {
                                backgroundColor: colors.palette.primary.dark2,
                                color: 'white',
                            },
                        },
                    },
                    '&.accountMenuItem:last-of-type': {
                        marginBottom: 0,
                    },
                },
            },
        },
        MuiFormControl: {
            styleOverrides: {
                root: {
                    marginBottom: '10px',
                    '&.radioGroupThemeControl': {
                        borderRadius: borderRadius.primary,
                        backgroundColor: 'transparent',
                        border: `1px solid ${colors.palette.gray.main}`,
                    },
                    '&.radioGroupThemeControl .MuiFormControlLabel-root': {
                        borderRadius: borderRadius.primary,
                    },
                    '&.radioGroupThemeControl.radioGroupControlActive': {
                        borderColor: colors.palette.primary.dark,
                    },
                    '&.radioGroupThemeControl:hover': {
                        borderRadius: borderRadius.primary,
                        borderColor: colors.palette.primary.main,
                        background: 'transparent',
                    },
                    '&.radioGroupThemeControl .MuiFormControlLabel-root:hover': {
                        background: 'transparent',
                        boxShadow: 'none',
                    },
                    '&.radioGroupThemeControl.radioGroupControlActive:hover .MuiFormControlLabel-root':
                        {
                            backgroundColor: addOpacityToThemeColor(
                                colors.palette.primary.main,
                                0.01,
                            ),
                        },
                    '&.radioGroupThemeControl .MuiFormControlLabel-root.Mui-disabled, &.radioGroupThemeControl .MuiFormControlLabel-root.Mui-disabled span':
                        {
                            cursor: 'default',
                        },
                    '.ProfileQuestionsContent &:has(:not([value=""]):not(:focus):invalid) .MuiInputLabel-root':
                        {
                            color: colors.other.text.error,
                        },
                },
            },
        },
        MuiInputLabel: {
            styleOverrides: {
                root: {
                    ...defaultTheme.MuiInputLabel.styleOverrides.root,
                    color: variables.colors.other.inputs.default,
                    '&.MuiInputLabel-shrink': {
                        transform: 'translate(0, -20px) scale(0.8)',
                        left: 0,
                    },
                    '&.Mui-focused': {
                        color: variables.colors.other.inputs.default,
                    },
                    '&.Mui-error': {
                        color: variables.colors.other.inputs.default,
                    },
                    '.MuiFormLabel-asterisk': {
                        color: variables.colors.error.main,
                        verticalAlign: 'top',
                        order: '-1',
                        paddingRight: '3px',
                        textAlign: 'left',
                        marginLeft: '0',
                    },
                },
            },
        },
        MuiInputBase: {
            styleOverrides: {
                root: {
                    fontFamily: fonts.additional,
                },
            },
        },
        MuiFormControlLabel: {
            styleOverrides: {
                ...defaultTheme.MuiFormControlLabel.styleOverrides,
                label: {
                    ...defaultTheme.MuiFormControlLabel.styleOverrides.label,
                    paddingTop: '8px',
                    fontSize: '1rem',
                },
                asterisk: {
                    ...defaultTheme.MuiFormControlLabel.styleOverrides.asterisk,
                    width: '6px',
                    height: '6px',
                    marginRight: '2px',
                },
            },
        },
        MuiCheckbox: {
            styleOverrides: {
                root: {
                    ...defaultTheme.MuiCheckbox.styleOverrides.root,
                    '&.Mui-checked': {
                        color: colors.palette.primary.dark,
                    },
                    '&:hover': {
                        backgroundColor: addOpacityToThemeColor(colors.palette.primary.dark, 0.04),
                    },
                },
            },
        },
        MuiRadio: {
            styleOverrides: {
                root: {
                    ...defaultTheme.MuiRadio.styleOverrides.root,
                    '&.Mui-checked': {
                        color: colors.palette.primary.dark,
                    },
                    '&:hover': {
                        backgroundColor: addOpacityToThemeColor(colors.palette.primary.dark, 0.04),
                    },
                    '&.Mui-disabled': {
                        color: colors.palette.gray.dark4,
                    },
                },
            },
        },
        MuiTouchRipple: {
            ...defaultTheme.MuiTouchRipple,
            styleOverrides: {
                ...defaultTheme.MuiTouchRipple.styleOverrides,
                root: {
                    ...defaultTheme.MuiTouchRipple.styleOverrides.root,
                    '.accountMenuItem &': {
                        display: 'none',
                    },
                },
            },
        },
        MuiFormHelperText: {
            styleOverrides: {
                root: {
                    ...defaultTheme.MuiFormHelperText.styleOverrides.root,
                    '&.Mui-error': {
                        ...defaultTheme.MuiFormHelperText.styleOverrides.root['&.Mui-error'],
                        marginLeft: '0',
                    },
                    '&.Mui-required': {
                        ...defaultTheme.MuiFormHelperText.styleOverrides.root['&.Mui-required'],
                        marginLeft: '0',
                    },
                },
            },
        },
        MuiAlert: {
            defaultProps: {
                iconMapping: {
                    success: SuccessIcon(),
                    warning: WarningIcon(),
                    error: ErrorIcon(),
                    info: InfoIcon(),
                },
            },
            styleOverrides: {
                message: {
                    ...defaultTheme.MuiAlert.styleOverrides.message,
                    padding: '8px 0',
                },
                filledSuccess: {
                    color: colors.other.text.success,
                    backgroundColor: colors.other.background.success,
                },
                filledError: {
                    color: colors.other.text.error,
                    backgroundColor: colors.other.background.error,
                },
                root: {
                    border: 'none',
                    '&.notificationAlert': {
                        border: `1px solid ${colors.other.text.lightGray}`,
                        position: 'relative',
                        borderTop: `1px solid ${colors.other.notifications.success}`,
                    },
                    '&.notificationAlertWarning': {
                        borderTop: `1px solid ${colors.other.notifications.warning}`,
                    },
                    '&.notificationAlertError': {
                        borderTop: `1px solid ${colors.other.notifications.error}`,
                    },
                    '&.notificationAlertInfo': {
                        borderTop: `1px solid ${colors.other.notifications.info}`,
                    },
                },
            },
        },
        MuiButton: {
            variants: [
                {
                    props: { variant: 'tertiary' },
                    style: {
                        height: '38px',
                        borderRadius: borderRadius.secondary,
                        background: 'transparent',
                        color: colors.palette.text.primary,
                        fontSize: '1rem',
                        border: '1px solid transparent',
                        boxShadow: boxShadows.buttonShadow,
                        '&.active': {
                            background: 'transparent',
                            color: colors.palette.primary.dark,
                            borderColor: colors.palette.primary.dark,
                        },
                        '&:hover, &.active:hover': {
                            color: colors.palette.primary.main,
                        },
                        '&.active:hover': {
                            borderColor: colors.palette.primary.main,
                        },
                    },
                },
                {
                    props: { variant: 'descriptive' },
                    style: {
                        backgroundColor: colors.palette.background.default,
                        borderRadius: borderRadius.primary,
                        border: `1px solid ${colors.palette.gray.main}`,
                        '.description': {
                            color: colors.palette.text.primary,
                        },
                        '.chevron': {
                            color: colors.palette.text.primary,
                        },
                        '&:hover': {
                            color: colors.palette.primary.main,
                            boxShadow: boxShadows.descriptiveShadow,
                            border: `1px solid ${colors.palette.primary.main}`,
                            backgroundColor: colors.palette.background.default,
                        },
                    },
                },
            ],
            styleOverrides: {
                root: {
                    fontFamily: fonts.primary,
                    fontSize: '1rem',
                    fontWeight: '400',
                    padding: '5px 20px',
                    borderRadius: borderRadius.secondary,
                    textTransform: 'none',
                    '&.surveyCardButton': {
                        color: colors.palette.primary.dark,
                        boxShadow: boxShadows.buttonShadow,
                        border: `1px solid ${colors.palette.primary.dark}`,
                        backgroundColor: 'transparent',
                        '&:hover': {
                            color: colors.palette.primary.main,
                            borderColor: colors.palette.primary.main,
                            backgroundColor: 'transparent',
                            boxShadow: boxShadows.buttonShadow,
                        },
                        '&:active': {
                            color: colors.palette.primary.dark3,
                            backgroundColor: 'transparent',
                            borderColor: colors.palette.primary.dark3,
                        },
                    },
                    '&.surveyLoadMoreButton': {
                        width: '100%',
                        color: colors.palette.secondary.dark,
                        boxShadow: boxShadows.buttonShadow,
                        border: `1px solid ${colors.palette.secondary.dark}`,
                        '&:hover': {
                            color: colors.palette.secondary.main,
                            borderColor: colors.palette.secondary.main,
                            backgroundColor: 'transparent',
                            boxShadow: boxShadows.buttonShadow,
                        },
                        '&:active': {
                            color: colors.palette.secondary.dark3,
                            backgroundColor: 'transparent',
                            borderColor: colors.palette.secondary.dark3,
                        },
                    },
                    '&.topMenuButton': {
                        margin: '0 2px',
                        padding: '5px 10px',
                        whiteSpace: 'nowrap',
                        borderRadius: borderRadius.secondary,
                        [breakpoints.up('xl')]: {
                            padding: '5px 20px',
                            margin: '0 8px',
                        },
                        '&.topMenuButton.selectOpinionTopMenuItem': {
                            display: 'flex',
                            gap: '0.75rem',
                            color: 'white',
                            backgroundColor: 'rgba(29, 81, 86, 1)',
                            ':hover': {
                                backgroundColor: 'rgba(74, 116, 120, 1)',
                            },
                        },
                    },
                    '&.language-button': {
                        fontSize: '1rem',
                        height: '38px',
                        fontWeight: '400',
                        color: colors.palette.text.primary,
                        '&:hover, &[aria-expanded="true"]': {
                            color: colors.palette.text.dark,
                        },
                    },
                    '&.mobileLocaleButton': {
                        marginRight: '1rem',
                    },
                },
                containedPrimary: {
                    color: 'white',
                    boxShadow: boxShadows.buttonShadow,
                    backgroundColor: colors.palette.primary.dark,
                    '&:hover': {
                        color: 'white',
                        backgroundColor: colors.palette.primary.main,
                        boxShadow: boxShadows.buttonShadow,
                    },
                    '&:active': {
                        color: 'white',
                        backgroundColor: colors.palette.primary.dark3,
                    },
                },
                containedSecondary: {
                    color: 'white',
                    boxShadow: boxShadows.buttonShadow,
                    backgroundColor: colors.palette.secondary.dark,
                    '&:hover': {
                        color: 'white',
                        backgroundColor: colors.palette.secondary.main,
                        boxShadow: boxShadows.buttonShadow,
                    },
                    '&:active': {
                        color: 'white',
                        backgroundColor: colors.palette.secondary.dark3,
                    },
                },
                containedSuccess: {
                    color: 'white',
                },
                outlinedPrimary: {
                    color: colors.palette.primary.dark,
                    boxShadow: boxShadows.buttonShadow,
                    borderColor: colors.palette.primary.dark,
                    '&:hover': {
                        color: colors.palette.primary.main,
                        borderColor: colors.palette.primary.main,
                        backgroundColor: 'transparent',
                        boxShadow: boxShadows.buttonShadow,
                    },
                    '&:active': {
                        color: colors.palette.primary.dark3,
                        backgroundColor: 'transparent',
                        borderColor: colors.palette.primary.dark3,
                    },
                },
                outlinedSecondary: {
                    color: colors.palette.secondary.dark,
                    boxShadow: boxShadows.buttonShadow,
                    borderColor: colors.palette.secondary.dark,
                    '&:hover': {
                        color: colors.palette.secondary.main,
                        borderColor: colors.palette.secondary.main,
                        backgroundColor: 'transparent',
                        boxShadow: boxShadows.buttonShadow,
                    },
                    '&:active': {
                        color: colors.palette.secondary.dark3,
                        backgroundColor: 'transparent',
                        borderColor: colors.palette.secondary.dark3,
                    },
                },
                textPrimary: {
                    color: colors.palette.primary.dark,
                    boxShadow: boxShadows.buttonShadow,
                    '&:hover': {
                        color: colors.palette.primary.main,
                    },
                    '&:active': {
                        color: colors.palette.primary.dark3,
                    },
                },
                textSecondary: {
                    color: colors.palette.secondary.dark,
                    boxShadow: boxShadows.buttonShadow,
                    '&:hover': {
                        color: colors.palette.secondary.main,
                    },
                    '&:active': {
                        color: colors.palette.secondary.dark3,
                    },
                },
                outlined: {
                    '&.loginButton, &.mobileLoginButton, &.logoutButton, &.mobileLogoutButton': {
                        color: 'white',
                        padding: '5px 15px',
                        height: '38px',
                        fontSize: '1rem',
                        fontWeight: '400',
                        lineHeight: '100%',
                        boxShadow: boxShadows.buttonShadow,
                        borderRadius: borderRadius.secondary,
                        backgroundColor: colors.palette.primary.dark,
                        borderColor: 'transparent',
                        '&:hover': {
                            color: 'white',
                            backgroundColor: colors.palette.primary.main,
                            boxShadow: boxShadows.buttonShadow,
                            borderColor: 'transparent',
                        },
                        '&:active': {
                            color: 'white',
                            backgroundColor: colors.palette.primary.dark3,
                            borderColor: 'transparent',
                        },
                    },
                    '&.mobileLoginButton, &.mobileLogoutButton': {
                        color: colors.palette.text.primary,
                        textIndent: '-9999rem',
                        padding: '8px',
                        minWidth: '0',
                        borderColor: 'transparent',
                        background: 'transparent',
                    },
                    '&.mobileLoginButton:hover, &.mobileLogoutButton:hover, &.mobileLoginButton:active, &.mobileLogoutButton:active':
                        {
                            color: colors.palette.text.primary,
                            borderColor: 'transparent',
                            background: 'transparent',
                        },
                    '&.mobileLoginButton .topmenuButtonIcon, &.mobileLogoutButton .topmenuButtonIcon':
                        {
                            color: 'currentColor',
                            transform: 'scale(1.55)',
                            marginLeft: '0.5rem',
                        },
                },
            },
        },
        MuiIconButton: {
            styleOverrides: {
                root: {
                    '&.mobileLocaleButton': {
                        color: colors.palette.text.primary,
                    },
                    '&.mobileCloseMenuButton': {
                        '.menuIcon div, .menuIcon:before, .menuIcon:after': {
                            background: colors.palette.text.primary,
                        },
                    },
                    '&.mobileMenuButton': {
                        width: '24px',
                        height: '30px',
                        color: colors.palette.text.primary,
                        fontSize: '1rem',
                        boxShadow: boxShadows.buttonShadow,
                        borderRadius: 0,
                        '&:active': {
                            color: colors.palette.primary.dark,
                        },
                        '.menuIcon': {
                            width: '24px',
                        },
                        '.menuIcon div, .menuIcon:before, .menuIcon:after': {
                            background: colors.palette.text.primary,
                            height: '2px',
                            borderRadius: '0',
                            margin: '6px 0',
                        },
                        '&:hover': {
                            '.menuIcon div, .menuIcon:before, .menuIcon:after': {
                                background: colors.palette.text.light,
                            },
                        },
                    },
                },
            },
        },
        MuiBadge: {
            styleOverrides: {
                root: {
                    '&.notificationBadge > .MuiBadge-badge': {
                        right: '-6px',
                        top: '3px',
                    },
                    '&.notificationBadgeWithQty > .MuiBadge-badge': {
                        width: '12px',
                        minWidth: '12px',
                        height: '12px',
                    },
                },
                badge: {
                    fontFamily: fonts.secondary,
                    fontWeight: '400',
                    boxShadow: 'none',
                },
            },
        },
        MuiTooltip: defaultTheme.MuiTooltip,
        MuiStep: {
            styleOverrides: {
                root: {
                    ...defaultTheme.MuiStep.styleOverrides.root,
                    color: addOpacityToThemeColor(colors.palette.tertiary.main, 0.1),
                    paddingLeft: '0px',
                    paddingRight: '0px',
                    '& .Mui-active': {
                        color: addOpacityToThemeColor(colors.palette.tertiary.main, 0.1),
                    },
                },
            },
        },
        MuiStepIcon: {
            styleOverrides: {
                root: {
                    ...defaultTheme.MuiStepIcon.styleOverrides.root,
                    color: 'transparent',
                    width: '1.4em',
                    height: '1.4em',
                    borderRadius: '50%',
                    backgroundColor: colors.palette.gray.light,
                    '> text': {
                        fontWeight: '400',
                        fill: colors.palette.gray.dark3,
                    },
                    '&.Mui-active': {
                        color: 'transparent',
                        backgroundColor: colors.palette.tertiary.main,
                        '> text': {
                            fill: 'white',
                        },
                    },
                    '&.Mui-completed': {
                        color: colors.palette.primary.dark,
                        background: `${colors.palette.primary.main} 50% 50% no-repeat url("data:image/svg+xml,%3Csvg fill='white' width='24px' height='24px' viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M103.99951,192.00012a7.97346,7.97346,0,0,1-5.65625-2.34277l-56-55.99561a7.99983,7.99983,0,1,1,11.3125-11.31445l50.34375,50.33935L210.34229,66.34778a8.00018,8.00018,0,0,1,11.31445,11.31347l-112,111.9961A7.97937,7.97937,0,0,1,103.99951,192.00012Z'/%3E%3C/svg%3E")`,
                        '> text': {
                            fill: 'white',
                        },
                    },
                },
                text: {
                    ...defaultTheme.MuiStepIcon.styleOverrides.text,
                    fill: colors.palette.tertiary.main,
                    fontWeight: '600',
                    '&.Mui-active': {
                        fill: colors.palette.tertiary.main,
                    },
                },
            },
        },
        MuiStepLabel: {
            styleOverrides: {
                root: defaultTheme.MuiStepLabel.styleOverrides.root,
                vertical: {
                    '.MuiStepLabel-label': {
                        ...defaultTheme.MuiStepLabel.styleOverrides.vertical['.MuiStepLabel-label'],
                        fontWeight: '400',
                        fontSize: '1rem',
                        '&.Mui-active': {
                            ...defaultTheme.MuiStepLabel.styleOverrides.vertical[
                                '.MuiStepLabel-label'
                            ]['&.Mui-active'],
                            color: colors.palette.tertiary.dark,
                        },
                    },
                },
                horizontal: {
                    '.MuiStepLabel-label': {
                        ...defaultTheme.MuiStepLabel.styleOverrides.horizontal[
                            '.MuiStepLabel-label'
                        ],
                        fontWeight: '600',
                        fontSize: '1.125rem',
                        marginLeft: '16px',
                        '&.Mui-active': {
                            ...defaultTheme.MuiStepLabel.styleOverrides.horizontal[
                                '.MuiStepLabel-label'
                            ]['&.Mui-active'],
                            fontWeight: '600',
                            color: colors.palette.tertiary.dark,
                        },
                        [breakpoints.up(420)]: {
                            '&.Mui-active': {
                                display: 'inline-block',
                            },
                        },
                    },
                },
            },
        },
        MuiStepConnector: {
            styleOverrides: {
                root: {
                    '.MuiStepConnector-line': {
                        border: '0',
                        height: '10px',
                        background: stepperLineBackground(colors.palette.gray.dark3),
                    },
                    '&.Mui-active, &.Mui-completed': {
                        '.MuiStepConnector-line': {
                            background: stepperLineBackground(colors.palette.primary.main),
                        },
                    },
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    ...defaultTheme.MuiCard.styleOverrides.root,
                    '&.surveyCard': {
                        borderRadius: '14px',
                        borderColor: colors.palette.gray.dark2,
                        '&:hover': {
                            borderColor: colors.palette.gray.dark3,
                        },
                    },
                    '&.infoCard': {
                        border: 'none',
                    },
                    '&.introRegistration': {
                        ...defaultTheme.MuiCard.styleOverrides.root['&.introRegistration'],
                        borderRadius: 0,
                        background: 'transparent',
                        overflow: 'visible',
                    },
                    '.nmSurveyCardTitle': {
                        background: colors.palette.gray.light,
                        color: colors.palette.primary.dark3,
                        fontWeight: '600',
                    },
                    '.nmSurveyCardBaseInfo': {
                        background: colors.palette.gray.light,
                        h6: {
                            fontWeight: 'normal',
                            fontSize: '0.875rem',
                            color: colors.palette.text.dark,
                        },
                    },
                    '&.referralCardWrapper': {
                        paddingTop: '14px',
                        '.referralDetailContent li': {
                            marginLeft: '1rem',
                            marginTop: '0.25rem',
                        },
                        '.referralActionButtonBlock': {
                            marginTop: '1rem',
                        },
                    },
                    '.nmSurveyCardDetails': {
                        h6: {
                            color: colors.palette.text.dark,
                            fontSize: '1rem',
                            '&.nmSurveyCardLabel': {
                                fontSize: '0.875rem',
                                color: 'rgba(127, 136, 152, 1)',
                            },
                        },
                    },
                    '.passwordRequirmentsTitle': {
                        '.MuiCardHeader-title': {
                            color: colors.palette.gray.dark4,
                        },
                        '.MuiCardHeader-avatar': {
                            marginRight: '8px',
                        },
                    },
                    '.passwordDescriptionContent ul': {
                        marginBottom: '0',
                    },
                },
            },
        },
        MuiDivider: {
            styleOverrides: {
                root: {
                    ...defaultTheme.MuiDivider.styleOverrides.root,
                    fontSize: '1rem',
                },
            },
        },
        MuiTabs: {
            styleOverrides: {
                flexContainer: {
                    gap: '8px',
                },
                root: {
                    ...defaultTheme.MuiTabs.styleOverrides.root,
                    backgroundColor: 'white',
                    boxShadow: boxShadows.buttonShadow,
                    width: '100%',
                    [breakpoints.up('sm')]: {
                        '&.MuiTabs-root': {
                            width: 'fit-content',
                        },
                    },
                },
            },
        },
        MuiTab: {
            styleOverrides: {
                root: {
                    padding: 8,
                    textTransform: 'none',
                    fontSize: '1rem',
                    fontWeight: '400',
                    minHeight: '42px',
                    borderRadius: borderRadius.primary,
                    color: colors.palette.text.primary,
                    transition: 'all 0.3s ease-in-out',
                    background: colors.palette.gray.light,
                    [breakpoints.up('sm')]: {
                        '&.MuiButtonBase-root': {
                            flexBasis: 'auto',
                            padding: '10px 16px',
                        },
                    },
                    '&.Mui-selected': {
                        ...defaultTheme.MuiTab.styleOverrides.root['&.Mui-selected'],
                        background: colors.palette.primary.dark,
                    },
                    '&.Mui-selected:hover': {
                        backgroundColor: colors.palette.primary.dark2,
                    },
                    '&.MuiButtonBase-root:not(.Mui-selected):hover': {
                        color: 'white',
                        background: colors.palette.primary.main,
                    },
                    '&.MuiButtonBase-root:active': {
                        color: 'white',
                        background: colors.palette.primary.dark3,
                    },
                },
            },
        },
        MuiTabPanel: defaultTheme.MuiTabPanel,
        MuiTable: {
            styleOverrides: {
                root: {
                    borderRadius: '0',
                },
            },
        },
        MuiTableHead: {
            styleOverrides: {
                root: {
                    ...defaultTheme.MuiTableHead.styleOverrides.root,
                    '& th': {
                        fontWeight: '400',
                        color: colors.palette.gray.dark4,
                    },
                },
            },
        },
        MuiTableCell: {
            styleOverrides: {
                root: {
                    ...defaultTheme.MuiTableCell.styleOverrides.root,
                    backgroundColor: 'transparent',
                    fontSize: '0.875rem',
                },
                head: defaultTheme.MuiTableCell.styleOverrides.head,
            },
        },
        MuiTableRow: {
            styleOverrides: {
                root: {
                    ...defaultTheme.MuiTableRow.styleOverrides.root,
                    '&:hover': {
                        '& > *:not(th)': {
                            color: colors.palette.tertiary.main,
                            '.MuiTypography-root': {
                                color: colors.palette.tertiary.main,
                            },
                        },
                    },
                    '&:nth-of-type(odd) td': {
                        backgroundColor: 'white',
                    },
                    '&:nth-of-type(even) td': {
                        backgroundColor: colors.other.table.evenRowHighlight,
                    },
                },
                head: defaultTheme.MuiTableRow.styleOverrides.head,
            },
        },
        MuiPaginationItem: {
            styleOverrides: {
                root: {
                    borderRadius: '50%',
                    marginBottom: '2px',
                    '&.Mui-selected': {
                        boxShadow: boxShadows.buttonShadow,
                        backgroundColor: colors.palette.primary.dark,
                    },
                    '&.Mui-selected:hover': {
                        backgroundColor: colors.palette.primary.main,
                        color: 'white',
                    },
                    '&.Mui-disabled': {
                        backgroundColor: colors.palette.gray.main,
                        color: colors.palette.gray.dark4,
                    },
                    margin: '0 8px',
                },
            },
        },
        MuiPickersPopper: defaultTheme.MuiPickersPopper,
        MuiPickersDay: defaultTheme.MuiPickersDay,
        MuiSkeleton: defaultTheme.MuiSkeleton,
        MuiSwitch: {
            styleOverrides: {
                switchBase: {
                    '& + .MuiSwitch-track': {
                        backgroundColor: colors.palette.gray.dark4,
                    },
                    '& .MuiSwitch-thumb': {
                        boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.2)',
                    },
                    '&.Mui-checked': {
                        '& + .MuiSwitch-track': {
                            backgroundColor: colors.palette.primary.dark,
                            opacity: 0.5,
                        },
                        '& .MuiSwitch-thumb': {
                            backgroundColor: colors.palette.primary.dark,
                            boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.2)',
                        },
                    },
                    '&.Mui-disabled': {
                        '& + .MuiSwitch-track': {
                            backgroundColor: colors.palette.gray.dark4,
                            opacity: 0.5,
                        },
                        '& .MuiSwitch-thumb': {
                            backgroundColor: 'rgba(240, 240, 240, 1)',
                        },
                    },
                },
            },
        },
    },
    shadows: Array(25).fill('none') as Shadows,
});

export default theme;
